import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import AddBoxIcon from '@mui/icons-material/AddBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Alert, IconButton, Snackbar } from "@mui/material";
import axios from 'axios';
import "./editpopup.css"; // Reusing the same CSS as EditPopup

const AddPopup = ({ title, initialContent = "", onAdd, onClose, label }) => {
  // State for single content (e.g., About, Company Name, Location)
  const [content, setContent] = useState(initialContent);

  // State for multiple entries (hobbies, interests, talents)
  const [hobbies, setHobbies] = useState(initialContent ? initialContent.split(",") : []);
  const [interests, setInterests] = useState(initialContent ? initialContent.split(",") : []);
  const [talents, setTalents] = useState(initialContent ? initialContent.split(",") : []);
  
  // State for new item entry
  const [newHobby, setNewHobby] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [newTalent, setNewTalent] = useState("");

  // State for location suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [error, setError] = useState('');
  const debounceTimeoutRef = useRef(null);

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setContent(initialContent);
    // Initialize the appropriate state based on the title
    if (title === "Edit Hobbies Section") {
      setHobbies(initialContent ? initialContent.split(",") : []);
    } else if (title === "Edit Interested In Section") {
      setInterests(initialContent ? initialContent.split(",") : []);
    } else if (title === "Edit Talents Section") {
      setTalents(initialContent ? initialContent.split(",") : []);
    }
  }, [initialContent, title]);

  useEffect(() => {
    if (title === "Edit Hobbies Section") {
      setIsValid(hobbies.length > 0);
    } else if (title === "Edit Interested In Section") {
      setIsValid(interests.length > 0);
    } else if (title === "Edit Talents Section") {
      setIsValid(talents.length > 0);
    } else if (title === "Edit Location") {
      setIsValid(selectedAddress || content !== "");
    } else {
      setIsValid(content !== "");
    }
  }, [content, hobbies, interests, talents, title, selectedAddress]);

  // Handlers for hobbies
  const handleAddHobby = () => {
    if (newHobby.trim() !== "") {
      setHobbies([...hobbies, newHobby.trim()]);
      setNewHobby("");
    }
  };

  const handleRemoveHobby = (index) => {
    const updatedHobbies = hobbies.filter((_, i) => i !== index);
    setHobbies(updatedHobbies);
  };

  // Handlers for interests
  const handleAddInterest = () => {
    if (newInterest.trim() !== "") {
      setInterests([...interests, newInterest.trim()]);
      setNewInterest("");
    }
  };

  const handleRemoveInterest = (index) => {
    const updatedInterests = interests.filter((_, i) => i !== index);
    setInterests(updatedInterests);
  };

  // Handlers for talents
  const handleAddTalent = () => {
    if (newTalent.trim() !== "") {
      setTalents([...talents, newTalent.trim()]);
      setNewTalent("");
    }
  };

  const handleRemoveTalent = (index) => {
    const updatedTalents = talents.filter((_, i) => i !== index);
    setTalents(updatedTalents);
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setContent(input);
    setSelectedAddress(null);
    setError('');
    
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    if (input.length > 2) {
      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(input);
      }, 300);
    } else {
      setSuggestions([]);
    }
  };

  const fetchSuggestions = async (query) => {
    try {
      const response = await axios.get(
        'https://nominatim.openstreetmap.org/search',
        {
          params: {
            q: query,
            format: 'json',
            addressdetails: 1,
            limit: 5,
            countrycodes: 'us'
          },
        }
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  };

  const handleSelectAddress = (suggestion) => {
    setSelectedAddress(suggestion);
    setContent(suggestion.display_name);
    setSuggestions([]);
  };

  const handleSubmit = () => {
    if (typeof onAdd === "function") {
      if (title === "Edit Hobbies Section") {
        onAdd(hobbies.join(","));
      } else if (title === "Edit Interested In Section") {
        onAdd(interests.join(","));
      } else if (title === "Edit Talents Section") {
        onAdd(talents.join(","));
      } else if (title === "Edit Location" && selectedAddress) {
        onAdd(selectedAddress.display_name);
      } else {
        onAdd(content);
      }
      onClose();
    } else {
      console.error("onAdd is not a function");
    }
  };

  // Function to render multi-entry field (for hobbies, interests, talents)
  const renderMultiEntryField = (type) => {
    let items, setNewItem, newItem, handleAdd, handleRemove, label;
    
    if (type === "hobbies") {
      items = hobbies;
      setNewItem = setNewHobby;
      newItem = newHobby;
      handleAdd = handleAddHobby;
      handleRemove = handleRemoveHobby;
      label = "Hobbies";
    } else if (type === "interests") {
      items = interests;
      setNewItem = setNewInterest;
      newItem = newInterest;
      handleAdd = handleAddInterest;
      handleRemove = handleRemoveInterest;
      label = "Interested In";
    } else if (type === "talents") {
      items = talents;
      setNewItem = setNewTalent;
      newItem = newTalent;
      handleAdd = handleAddTalent;
      handleRemove = handleRemoveTalent;
      label = "Talents";
    }

    return (
      <div className="edit-popup-field">
        <label>{label}</label>
        <div style={{display: "flex", gap: 3}}>
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={`Enter a ${label.toLowerCase().slice(0, -1)}`}
          />
          <IconButton onClick={handleAdd}>
            <AddCircleIcon color="primary" />
          </IconButton>
        </div>
        <div className="hobbies-list">
          {items.map((item, index) => (
            <div key={index} className="hobby-item">
              <span>{item}</span>
              <button onClick={() => handleRemove(index)}>×</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="edit-popup">
      <div className="edit-popup-content">
        <div className="edit-popup-header">
          <h2>{title}</h2>
        </div>
        <div className="edit-popup-body">
          {title === "Edit Hobbies Section" ? (
            renderMultiEntryField("hobbies")
          ) : title === "Edit Interested In Section" ? (
            renderMultiEntryField("interests")
          ) : title === "Edit Talents Section" ? (
            renderMultiEntryField("talents")
          ) : title === "Edit Location" ? (
            <div className="edit-popup-field">
              <label>Location</label>
              <input
                type="text"
                name="Content"
                value={content}
                onChange={handleAddressChange}
                placeholder="Type to search locations..."
              />
              {error && <div className="error-message">{error}</div>}
              {suggestions.length > 0 && (
                <div className="location-suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={index} 
                      className="suggestion-item"
                      onClick={() => handleSelectAddress(suggestion)}
                    >
                      {suggestion.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="edit-popup-field">
              <label>
                {label || (title === "Edit About Section"
                  ? "About"
                  : title === "Edit Company Name"
                  ? "Company Name"
                  : "Content")}
              </label>
              {title === "Edit About Section" ? (
                <textarea
                  name="Content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{ width: "100%", height: "100px" }}
                />
              ) : (
                <input
                  type="text"
                  name="Content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
              )}
            </div>
          )}
        </div>
        <div className="edit-popup-footer">
          {isValid && <button onClick={handleSubmit}>Save</button>}
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

AddPopup.propTypes = {
  title: PropTypes.string.isRequired,
  initialContent: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default AddPopup;



// import React, { useState, useEffect } from "react";
// import PropTypes from "prop-types";
// import AddBoxIcon from '@mui/icons-material/AddBox';
// import AddCircleIcon from '@mui/icons-material/AddCircle';
// import { Alert, IconButton, Snackbar } from "@mui/material";
// import "./editpopup.css"; // Reusing the same CSS as EditPopup

// const AddPopup = ({ title, initialContent = "", onAdd, onClose }) => {
//   // State for single content (e.g., About, Company Name, Location)
//   const [content, setContent] = useState(initialContent);

//   // State for hobbies (if title is "Edit Hobbies Section")
//   const [hobbies, setHobbies] = useState(
//     initialContent ? initialContent.split(",") : []
//   );
//   const [newHobby, setNewHobby] = useState("");

//   const [isValid, setIsValid] = useState(false);

//   useEffect(() => {
//     setContent(initialContent);
//     if (title === "Edit Hobbies Section") {
//       setHobbies(initialContent ? initialContent.split(",") : []);
//     }
//   }, [initialContent, title]);

//   useEffect(() => {
//     if (title === "Edit Hobbies Section") {
//       setIsValid(hobbies.length > 0);
//     } else {
//       setIsValid(content !== "");
//     }
//   }, [content, hobbies, title]);

//   const handleAddHobby = () => {
//     if (newHobby.trim() !== "") {
//       setHobbies([...hobbies, newHobby.trim()]);
//       setNewHobby("");
//     }
//   };

//   const handleRemoveHobby = (index) => {
//     const updatedHobbies = hobbies.filter((_, i) => i !== index);
//     setHobbies(updatedHobbies);
//   };

//   const handleSubmit = () => {
//     if (typeof onAdd === "function") {
//       if (title === "Edit Hobbies Section") {
//         // Save hobbies as a comma-separated string
//         onAdd(hobbies.join(","));
//       } else {
//         onAdd(content);
//       }
//       onClose();
//     } else {
//       console.error("onAdd is not a function");
//     }
//   };

//   return (
//     <div className="edit-popup">
//       <div className="edit-popup-content">
//         <div className="edit-popup-header">
//           <h2>{title}</h2>
//         </div>
//         <div className="edit-popup-body">
//           {title === "Edit Hobbies Section" ? (
//             <div className="edit-popup-field">
//               <label>Hobbies</label>
//               <div style={{display: "flex", gap: 3}}>
//                 <input
//                   type="text"
//                   value={newHobby}
//                   onChange={(e) => setNewHobby(e.target.value)}
//                   placeholder="Enter a hobby"
//                 />
//                 <IconButton onClick={handleAddHobby}>
//                   <AddCircleIcon color="primary" />
//                 </IconButton>
//               </div>
//               <div className="hobbies-list">
//                 {hobbies.map((hobby, index) => (
//                   <div key={index} className="hobby-item">
//                     <span>{hobby}</span>
//                     <button onClick={() => handleRemoveHobby(index)}>×</button>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           ) : (
//             <div className="edit-popup-field">
//               <label>
//                 {title === "Edit About Section"
//                   ? "About"
//                   : title === "Edit Company Name"
//                   ? "Company Name"
//                   : title === "Edit Location"
//                   ? "Location"
//                   : "Content"}
//               </label>
//               {title === "Edit About Section" ? (
//                 <textarea
//                   name="Content"
//                   value={content}
//                   onChange={(e) => setContent(e.target.value)}
//                   style={{ width: "100%", height: "100px" }}
//                 />
//               ) : (
//                 <input
//                   type="text"
//                   name="Content"
//                   value={content}
//                   onChange={(e) => setContent(e.target.value)}
//                 />
//               )}
//             </div>
//           )}
//         </div>
//         <div className="edit-popup-footer">
//           {isValid && <button onClick={handleSubmit}>Save</button>}
//           <button className="cancel-button" onClick={onClose}>
//             Cancel
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// AddPopup.propTypes = {
//   title: PropTypes.string.isRequired,
//   initialContent: PropTypes.string,
//   onAdd: PropTypes.func.isRequired,
//   onClose: PropTypes.func.isRequired,
// };

// export default AddPopup;